import {
  API_BASE_URL,
  API_ENDPOINTS,
  getKeycloakAdminLoginURL,
  isCustomerSubdomain,
  getCustomerIdFromSubdomain,
  getUsersEndpoint,
  getGroupsEndpoint,
  getGroupMembersEndpoint,
  getIdentityProvidersEndpoint,
  getGoogleIdPEndpoint,
  getGoogleIdPValidationEndpoint,
  getMicrosoftIdPEndpoint,
  getMicrosoftIdPValidationEndpoint,
  getIdPStatusEndpoint,
  getCustomIdPEndpoint,
  getCustomIdPValidationEndpoint,
} from '../utils/constants';
import { isReadOnlyAdmin } from '../hooks/useReadOnlyMode';

class ApiService {
  constructor() {
    // Add request cache to prevent duplicate requests
    this.requestCache = new Map();
    this.authToken = localStorage.getItem('authToken');
    this.tokenExpiry = localStorage.getItem('tokenExpiry');
    // Separate application admin token
    this.appAdminToken = localStorage.getItem('appAdminToken');
    this.appAdminTokenExpiry = localStorage.getItem('appAdminTokenExpiry');
    // Refresh tokens
    this.refreshToken = localStorage.getItem('refreshToken');
    this.refreshTokenExpiry = localStorage.getItem('refreshTokenExpiry');
    this.appAdminRefreshToken = localStorage.getItem('appAdminRefreshToken');
    this.appAdminRefreshTokenExpiry = localStorage.getItem('appAdminRefreshTokenExpiry');
    // Track refresh in progress to prevent duplicate refresh calls
    this.refreshInProgress = null;
  }

  // Application Admin Authentication methods
  setAppAdminToken(token, expiry) {
    console.log("Setting app admin token in API service");
    this.appAdminToken = token;
    this.appAdminTokenExpiry = expiry;
    localStorage.setItem('appAdminToken', token);
    localStorage.setItem('appAdminTokenExpiry', expiry);
  }

  clearAppAdminToken() {
    this.appAdminToken = null;
    this.appAdminTokenExpiry = null;
    localStorage.removeItem('appAdminToken');
    localStorage.removeItem('appAdminTokenExpiry');
  }

  isAppAdminTokenValid() {
    if (!this.appAdminToken || !this.appAdminTokenExpiry) return false;
    return new Date() < new Date(this.appAdminTokenExpiry);
  }

  // Authentication helper methods
  setAuthToken(token, expiry) {
    console.log("Setting auth token in API service");
    this.authToken = token;
    this.tokenExpiry = expiry;
    localStorage.setItem('authToken', token);
    localStorage.setItem('tokenExpiry', expiry);
  }

  clearAuthToken() {
    this.authToken = null;
    this.tokenExpiry = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenExpiry');
  }

  isTokenValid() {
    if (!this.authToken || !this.tokenExpiry) return false;
    return new Date() < new Date(this.tokenExpiry);
  }

  // Token pair management methods
  setTokenPair(accessToken, accessExpiry, refreshToken, refreshExpiry) {
    this.setAuthToken(accessToken, accessExpiry);
    this.refreshToken = refreshToken;
    this.refreshTokenExpiry = refreshExpiry;
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('refreshTokenExpiry', refreshExpiry);
  }

  setAppAdminTokenPair(accessToken, accessExpiry, refreshToken, refreshExpiry) {
    this.setAppAdminToken(accessToken, accessExpiry);
    this.appAdminRefreshToken = refreshToken;
    this.appAdminRefreshTokenExpiry = refreshExpiry;
    localStorage.setItem('appAdminRefreshToken', refreshToken);
    localStorage.setItem('appAdminRefreshTokenExpiry', refreshExpiry);
  }

  clearRefreshToken() {
    this.refreshToken = null;
    this.refreshTokenExpiry = null;
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('refreshTokenExpiry');
  }

  clearAppAdminRefreshToken() {
    this.appAdminRefreshToken = null;
    this.appAdminRefreshTokenExpiry = null;
    localStorage.removeItem('appAdminRefreshToken');
    localStorage.removeItem('appAdminRefreshTokenExpiry');
  }

  clearAllTokens() {
    this.clearAuthToken();
    this.clearRefreshToken();
    this.clearAppAdminToken();
    this.clearAppAdminRefreshToken();
    localStorage.removeItem('adminDetails');
    localStorage.removeItem('appAdminDetails');
  }

  isRefreshTokenValid() {
    if (!this.refreshToken || !this.refreshTokenExpiry) return false;
    // Add 30 second buffer
    return new Date() < new Date(new Date(this.refreshTokenExpiry).getTime() - 30000);
  }

  isAppAdminRefreshTokenValid() {
    if (!this.appAdminRefreshToken || !this.appAdminRefreshTokenExpiry) return false;
    return new Date() < new Date(new Date(this.appAdminRefreshTokenExpiry).getTime() - 30000);
  }

  async attemptTokenRefresh() {
    // Prevent multiple simultaneous refresh attempts
    if (this.refreshInProgress) {
      return this.refreshInProgress;
    }

    const customerId = getCustomerIdFromSubdomain();

    this.refreshInProgress = (async () => {
      try {
        if (customerId) {
          // Customer subdomain - refresh customer token
          if (!this.isRefreshTokenValid()) {
            console.log('Refresh token expired or invalid');
            return false;
          }
          const response = await fetch(`${API_BASE_URL}/api/v1/customers/${customerId}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: this.refreshToken }),
          });
          if (!response.ok) {
            console.log('Refresh request failed:', response.status);
            return false;
          }
          const data = await response.json();
          if (data.success && data.data) {
            this.setTokenPair(
              data.data.accessToken,
              data.data.accessExpiresAt,
              data.data.refreshToken,
              data.data.refreshExpiresAt
            );
            // Keep adminDetails.role in sync with the refreshed token so role changes
            // (read_only <-> full admin, or demotion) reflect in the UI on refresh, not
            // only after a full re-login. The backend already honors the token's role.
            try {
              const claims = JSON.parse(atob(data.data.accessToken.split('.')[1]));
              const stored = localStorage.getItem('adminDetails');
              if (stored && claims && claims.role) {
                const ad = JSON.parse(stored);
                if (ad.role !== claims.role) {
                  ad.role = claims.role;
                  localStorage.setItem('adminDetails', JSON.stringify(ad));
                }
              }
            } catch (e) { /* non-fatal: token still refreshed successfully */ }
            console.log('Token refreshed successfully');
            return true;
          }
          return false;
        } else {
          // App admin - refresh app admin token
          if (!this.isAppAdminRefreshTokenValid()) {
            console.log('App admin refresh token expired or invalid');
            return false;
          }
          const response = await fetch(`${API_BASE_URL}/api/v1/app-admin/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: this.appAdminRefreshToken }),
          });
          if (!response.ok) {
            console.log('App admin refresh request failed:', response.status);
            return false;
          }
          const data = await response.json();
          if (data.success && data.data) {
            this.setAppAdminTokenPair(
              data.data.accessToken,
              data.data.accessExpiresAt,
              data.data.refreshToken,
              data.data.refreshExpiresAt
            );
            console.log('App admin token refreshed successfully');
            return true;
          }
          return false;
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        return false;
      } finally {
        this.refreshInProgress = null;
      }
    })();

    return this.refreshInProgress;
  }

  getAuthHeaders() {
    // Use app admin token if we're not on customer subdomain and have valid app admin token
    if (!isCustomerSubdomain() && this.isAppAdminTokenValid()) {
      return { 'Authorization': `Bearer ${this.appAdminToken}` };
    }
    // Use customer admin token if we're on customer subdomain and have valid token
    if (isCustomerSubdomain() && this.isTokenValid()) {
      return { 'Authorization': `Bearer ${this.authToken}` };
    }
    return {};
  }

  // Read-only admins may not mutate Prism admin-portal resources. This is a UX
  // safety net only — the backend EnforceReadOnly middleware is authoritative.
  // It never touches the separate JIT portal (/api/v1/jit/, different token/path),
  // nor self-service auth/session endpoints. Admin-portal CloudTrail-management
  // mutations live under /api/v1/customers/ and ARE intentionally blocked.
  _isReadOnlyBlocked(method, url) {
    const mutating = ['POST', 'PUT', 'PATCH', 'DELETE'].includes((method || 'GET').toUpperCase());
    if (!mutating) return false;
    if (!isReadOnlyAdmin()) return false;
    if (!url.includes('/api/v1/customers/')) return false; // only the admin portal API
    const path = url.split('?')[0];
    // Self-service auth/session endpoints a read-only admin may still call.
    const selfService = ['/auth/refresh', '/auth/logout', '/auth/token-exchange', '/auth/mfa/setup', '/auth/sso-callback', '/auth/change-password'];
    if (selfService.some((s) => path.includes(s))) return false;
    // Reads served over POST (so read-only admins can still VIEW these pages). Matched
    // by exact path suffix so e.g. '/org/policies' (list) does NOT match
    // '/org/policies/create' (write), which must stay blocked. Keep in sync with the
    // backend readOnlyReadViaPostAllowlist.
    const readViaPost = [
      '/org/structure', '/org/enabled-policy-types', '/org/policies',
      '/org/policies/describe', '/org/policies/targets', '/org/policies/for-target',
      '/org/policies/count-for-target', '/org/policies/tags',
      '/contacts/get', '/billing/status',
    ];
    if (readViaPost.some((s) => path.endsWith(s))) return false;
    return true;
  }

  async makeRequest(url, options = {}) {
    if (this._isReadOnlyBlocked(options.method, url)) {
      return Promise.reject(new Error("Read-only access — you don't have permission to make changes."));
    }

    // Create a cache key for GET requests
    const cacheKey = `${options.method || 'GET'}:${url}`;
    const isGetRequest = (!options.method || options.method === 'GET');

    // For GET requests, check if there's already a pending request
    if (isGetRequest && this.requestCache.has(cacheKey)) {
      return this.requestCache.get(cacheKey);
    }

    const requestPromise = this._makeActualRequest(url, options);

    // Cache GET requests while they're pending
    if (isGetRequest) {
      this.requestCache.set(cacheKey, requestPromise);

      // Clean up cache after request completes
      requestPromise.finally(() => {
        this.requestCache.delete(cacheKey);
      });
    }

    return requestPromise;
  }

  async _makeActualRequest(url, options = {}, isRetry = false) {
    try {
      // Special handling for MFA setup and refresh endpoints - don't require auth
      const isMFASetup = url.includes('/mfa/setup');
      const isRefreshEndpoint = url.includes('/auth/refresh');

      // Combine auth headers with any provided headers, but skip auth for MFA setup and refresh
      const isFormData = options.isFormData || (options.body instanceof FormData);
      const headers = {
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
        ...(isMFASetup || isRefreshEndpoint ? {} : this.getAuthHeaders()),
        ...options.headers,
      };

      // Don't include isFormData in fetch options
      const { isFormData: _, ...fetchOptions } = options;

      const response = await fetch(`${API_BASE_URL}${url}`, {
        ...fetchOptions,
        headers,
      });

      // Handle DELETE responses that might be empty
      let data;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        // For DELETE operations that return empty responses
        data = { success: response.ok, message: response.ok ? 'Operation completed successfully' : 'Operation failed' };
      }

      // Handle 401 with automatic token refresh
      if (response.status === 401 && !isRetry && !isRefreshEndpoint) {
        console.log('Got 401, attempting token refresh...');
        const refreshed = await this.attemptTokenRefresh();
        if (refreshed) {
          console.log('Token refreshed successfully, retrying request...');
          return this._makeActualRequest(url, options, true);
        }

        // Refresh failed - clear all tokens and notify
        console.log('Token refresh failed, clearing tokens');
        this.clearAllTokens();
        window.dispatchEvent(new CustomEvent('auth-error', {
          detail: { message: 'Session expired - please login again' }
        }));
        throw new Error('Session expired - please login again');
      }

      // Handle error status codes
      if (!response.ok) {
        throw new Error(data.message || data.error || 'An unexpected error occurred. Please try again later.');
      }

      return data;
    } catch (error) {
      // Don't wrap already formatted error messages
      if (error.message && !error.message.includes('API request failed')) {
        throw error;
      }
      throw new Error(`API request failed: ${error.message}`);
    }
  }

  // Application Admin API methods
  async checkAppAdminStatus() {
    return this.makeRequest(API_ENDPOINTS.APP_ADMIN_STATUS);
  }

  async initializeAppAdmin(password) {
    return this.makeRequest(API_ENDPOINTS.APP_ADMIN_INIT, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async appAdminLogin(username, password, totpCode = '') {
    const response = await this.makeRequest(API_ENDPOINTS.APP_ADMIN_LOGIN, {
      method: 'POST',
      body: JSON.stringify({ username, password, totpCode }),
    });

    // Store app admin token if login successful
    if (response.success && response.data) {
      // Handle new token pair format
      if (response.data.accessToken && response.data.refreshToken) {
        this.setAppAdminTokenPair(
          response.data.accessToken,
          response.data.accessExpiresAt,
          response.data.refreshToken,
          response.data.refreshExpiresAt
        );
      }
      // Backward compatibility: handle old single-token response
      else if (response.data.token) {
        this.setAppAdminToken(response.data.token, response.data.expiresAt);
      }

      // Store app admin details
      const appAdminDetails = {
        username: response.data.username,
        isAppAdmin: true
      };
      localStorage.setItem('appAdminDetails', JSON.stringify(appAdminDetails));
    }

    return response;
  }

  async getAppAdminMe() {
    return this.makeRequest(API_ENDPOINTS.APP_ADMIN_ME);
  }

  // Helper method to check if current user is app admin
  isAppAdmin() {
    try {
      const appAdminDetails = localStorage.getItem('appAdminDetails');
      if (appAdminDetails) {
        const parsed = JSON.parse(appAdminDetails);
        return parsed.isAppAdmin === true;
      }
    } catch (error) {
      console.error('Error checking app admin status:', error);
    }
    return false;
  }

  // Application Admin MFA API methods
  async getAppAdminMFAStatus() {
    return this.makeRequest(API_ENDPOINTS.APP_ADMIN_MFA_STATUS);
  }

  async completeAppAdminMFASetup(totpCode) {
    return this.makeRequest(API_ENDPOINTS.APP_ADMIN_MFA_SETUP, {
      method: 'POST',
      body: JSON.stringify({ totpCode }),
    });
  }

  async disableAppAdminMFA(currentPassword, totpCode) {
    return this.makeRequest(API_ENDPOINTS.APP_ADMIN_MFA_DISABLE, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, totpCode }),
    });
  }

  // Customer Admin Authentication API methods (existing methods with customer context)
  async checkAuthStatus() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/auth/status`);
  }

  async initializeAdmin(password) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/initialize-admin`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  async login(username, password, totpCode = '') {
    const customerId = getCustomerIdFromSubdomain();
    const endpoint = `/api/v1/customers/${customerId}/auth/login`;

    const response = await this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify({ username, password, totpCode }),
    });

    // Store auth token if login successful
    if (response.success && response.data) {
      // Handle new token pair format
      if (response.data.accessToken && response.data.refreshToken) {
        this.setTokenPair(
          response.data.accessToken,
          response.data.accessExpiresAt,
          response.data.refreshToken,
          response.data.refreshExpiresAt
        );
      }
      // Backward compatibility: handle old single-token response
      else if (response.data.token) {
        this.setAuthToken(response.data.token, response.data.expiresAt);
      }

      // Store admin details for frontend use
      const adminDetails = {
        username: response.data.username,
        role: response.data.role,
        isAdmin: response.data.isAdmin,
        customerId: customerId
      };
      localStorage.setItem('adminDetails', JSON.stringify(adminDetails));
    }

    return response;
  }

  // Customer login method - add this method if missing or update existing one
  async customerLogin(customerId, username, password, totpCode = '') {
    try {
      const response = await this.makeRequest(`/api/v1/customers/${customerId}/auth/login`, {
        method: 'POST',
        body: JSON.stringify({ username, password, totpCode }),
      });

      // The response structure is: { success: true, message: "...", data: { actual login response } }
      // We need to handle the nested structure properly
      if (response.success && response.data) {
        const loginData = response.data;

        // Handle new token pair format
        if (loginData.accessToken && loginData.refreshToken) {
          this.setTokenPair(
            loginData.accessToken,
            loginData.accessExpiresAt,
            loginData.refreshToken,
            loginData.refreshExpiresAt
          );
        }
        // Backward compatibility: handle old single-token response
        else if (loginData.token) {
          this.setAuthToken(loginData.token, loginData.expiresAt);
        }

        // Store customer admin details for frontend use
        const adminDetails = {
          username: loginData.username,
          role: loginData.role,
          isAdmin: loginData.isAdmin,
          customerId: customerId
        };
        localStorage.setItem('adminDetails', JSON.stringify(adminDetails));

        // Return the actual login data, not the wrapper
        return {
          success: true,
          data: loginData
        };
      }

      return response;
    } catch (error) {
      console.error('Customer login error:', error);
      throw error;
    }
  }

  // Helper method to get current user's role
  getRole() {
    try {
      const adminDetails = localStorage.getItem('adminDetails');
      if (adminDetails) {
        const parsed = JSON.parse(adminDetails);
        return parsed.role || '';
      }
    } catch (error) {
      console.error('Error getting user role:', error);
    }
    return '';
  }

  // Helper method to check if current user is super admin (backward compat)
  isSuperAdmin() {
    return this.getRole() === 'admin';
  }

  async changePassword(currentPassword, newPassword) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/auth/change-password`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  // MFA API methods
  async getMFAStatus() {
    // Use app admin MFA endpoint if not on customer subdomain
    if (!isCustomerSubdomain()) {
      return this.makeRequest(API_ENDPOINTS.APP_ADMIN_MFA_STATUS);
    }
    // For customer context, use customer-specific MFA status endpoint
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/auth/mfa/status`);
  }

  async completeMFASetup(totpCode) {
    // For MFA setup during initialization, don't include auth headers
    // The backend will determine context based on the endpoint
    const headers = { 'Content-Type': 'application/json' };

    // Check if we're in customer context
    const customerId = getCustomerIdFromSubdomain();

    // Use customer-specific MFA setup endpoint if we have a customerId
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/auth/mfa/setup`, {
        method: 'POST',
        headers: headers, // Don't include auth headers for initial setup
        body: JSON.stringify({ totpCode }),
      });
    }

    // Use app admin MFA endpoint if not on customer subdomain
    return this.makeRequest(API_ENDPOINTS.APP_ADMIN_MFA_SETUP, {
      method: 'POST',
      headers: headers, // Don't include auth headers for initial setup
      body: JSON.stringify({ totpCode }),
    });
  }

  async disableMFA(currentPassword, totpCode) {
    // Use app admin MFA endpoint if not on customer subdomain
    if (!isCustomerSubdomain()) {
      return this.makeRequest(API_ENDPOINTS.APP_ADMIN_MFA_DISABLE, {
        method: 'POST',
        body: JSON.stringify({ currentPassword, totpCode }),
      });
    }

    // For customer context, use customer-specific MFA disable endpoint
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/auth/mfa/disable`, {
      method: 'POST',
      body: JSON.stringify({ currentPassword, totpCode }),
    });
  }

  // SAML SSO Login
  async initiateKeycloakSAMLLogin() {
    // This will redirect to Keycloak SAML IdP
    const keycloakLoginURL = getKeycloakAdminLoginURL();
    window.location.href = keycloakLoginURL;
  }

  async logout() {
    const customerId = getCustomerIdFromSubdomain();

    try {
      if (customerId) {
        // Customer subdomain - call customer logout endpoint
        if (this.refreshToken) {
          await fetch(`${API_BASE_URL}/api/v1/customers/${customerId}/auth/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...this.getAuthHeaders()
            },
            body: JSON.stringify({ refreshToken: this.refreshToken }),
          });
        }
      } else {
        // App admin - call app admin logout endpoint
        if (this.appAdminRefreshToken) {
          await fetch(`${API_BASE_URL}/api/v1/app-admin/logout`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...this.getAuthHeaders()
            },
            body: JSON.stringify({ refreshToken: this.appAdminRefreshToken }),
          });
        }
      }
    } catch (error) {
      console.warn('Logout request failed, clearing local tokens anyway:', error);
    }

    // Always clear all tokens locally regardless of server response
    this.clearAllTokens();
    return { success: true, message: 'Logged out successfully' };
  }

  // User operations
  async getAllUsers(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest(`${getUsersEndpoint()}${queryString}`);
  }

  async getUser(username) {
    const endpoint = `${getUsersEndpoint()}/${encodeURIComponent(username)}`;
    return this.makeRequest(endpoint);
  }

  async createUser(userData) {
    return this.makeRequest(getUsersEndpoint(), {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(username, userData) {
    const endpoint = `${getUsersEndpoint()}/${encodeURIComponent(username)}`;
    return this.makeRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(username) {
    const endpoint = `${getUsersEndpoint()}/${encodeURIComponent(username)}`;
    return this.makeRequest(endpoint, {
      method: 'DELETE',
    });
  }

  // Group operations
  async getAllGroups(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest(`${getGroupsEndpoint()}${queryString}`);
  }

  async getGroup(groupName) {
    const endpoint = `${getGroupsEndpoint()}/${encodeURIComponent(groupName)}`;
    return this.makeRequest(endpoint);
  }

  async createGroup(groupData) {
    return this.makeRequest(getGroupsEndpoint(), {
      method: 'POST',
      body: JSON.stringify(groupData),
    });
  }

  async deleteGroup(groupName) {
    const endpoint = `${getGroupsEndpoint()}/${encodeURIComponent(groupName)}`;
    return this.makeRequest(endpoint, {
      method: 'DELETE',
    });
  }

  // Group membership operations
  async getGroupMembers(groupName) {
    try {
      return this.makeRequest(getGroupMembersEndpoint(groupName));
    } catch (error) {
      // If not found, return empty array instead of error
      if (error.message.includes('not found')) {
        return {
          success: true,
          message: "No members found",
          data: { members: [] }
        };
      }
      throw error;
    }
  }

  async assignUsersToGroup(groupName, users) {
    console.log(`API call: assignUsersToGroup - ${groupName}`, { users });

    const payload = JSON.stringify({ users });
    console.log("Request payload:", payload);

    return this.makeRequest(getGroupMembersEndpoint(groupName), {
      method: 'POST',
      body: payload,
    });
  }

  async removeUsersFromGroup(groupName, users) {
    return this.makeRequest(getGroupMembersEndpoint(groupName), {
      method: 'DELETE',
      body: JSON.stringify({ users }),
    });
  }

  // Permission Set operations
  async getAllPermissionSets(params = {}) {
    const customerId = getCustomerIdFromSubdomain();
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-sets${queryString}`);
  }

  async getPermissionSet(id) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-sets/${id}`);
  }

  async createPermissionSet(permissionSetData) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-sets`, {
      method: 'POST',
      body: JSON.stringify(permissionSetData),
    });
  }

  async updatePermissionSet(id, permissionSetData) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-sets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(permissionSetData),
    });
  }

  async deletePermissionSet(id) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-sets/${id}`, {
      method: 'DELETE',
    });
  }

  // Customer Managed Policy operations
  async listCustomerManagedPolicies(params = {}) {
    const customerId = getCustomerIdFromSubdomain();
    const queryString = new URLSearchParams(params).toString();
    const suffix = queryString ? `?${queryString}` : '';
    return this.makeRequest(`/api/v1/customers/${customerId}/customer-managed-policies${suffix}`);
  }

  async getCustomerManagedPolicy(id) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/customer-managed-policies/${id}`);
  }

  async createCustomerManagedPolicy(body) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/customer-managed-policies`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async updateCustomerManagedPolicy(id, body) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/customer-managed-policies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  async deleteCustomerManagedPolicy(id) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/customer-managed-policies/${id}`, {
      method: 'DELETE',
    });
  }

  async getCustomerManagedPolicyUsage(id) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/customer-managed-policies/${id}/usage`);
  }

  async listCustomerManagedPolicyRollouts(id) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/customer-managed-policies/${id}/rollouts`);
  }

  async retryFailedCustomerManagedPolicyRollouts(id) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/customer-managed-policies/${id}/rollouts/retry`, {
      method: 'POST',
    });
  }

  // AWS Managed Policy operations
  async getAwsManagedPolicies(onlyAttachable = true) {
    // AWS managed policies are global and not customer-specific
    return this.makeRequest(`/api/v1/aws/managed-policies?onlyAttachable=${onlyAttachable}`);
  }

  async getAwsManagedPolicy(arn) {
    const encodedArn = encodeURIComponent(arn);
    // AWS managed policies are global and not customer-specific
    return this.makeRequest(`/api/v1/aws/managed-policies/${encodedArn}`);
  }

  // Add the missing validateIAMPolicy method
  async validateIAMPolicy(policyDocument, policyType = 'IDENTITY_POLICY') {
    // Policy validation is also global and not customer-specific
    return this.makeRequest('/api/v1/aws/validate-policy', {
      method: 'POST',
      body: JSON.stringify({
        policyDocument,
        policyType
      }),
    });
  }

  // Permission Set Assignment operations
  async getAllPermissionSetAssignments(filters = {}) {
    const customerId = getCustomerIdFromSubdomain();
    const queryParams = new URLSearchParams();
    if (filters.username) queryParams.append('username', filters.username);
    if (filters.accountId) queryParams.append('accountId', filters.accountId);
    if (filters.permissionSetId) queryParams.append('permissionSetId', filters.permissionSetId);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-set-assignments${queryString}`);
  }

  async getPermissionSetAssignment(id) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-set-assignments/${id}`);
  }

  async createPermissionSetAssignment(assignmentData) {
    const customerId = getCustomerIdFromSubdomain();
    // Ensure accountId is sent as array (accountIds)
    const payload = {
      ...assignmentData,
      accountIds: Array.isArray(assignmentData.accountId)
        ? assignmentData.accountId
        : [assignmentData.accountId]
    };
    // Remove the single accountId field if it exists
    delete payload.accountId;
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-set-assignments`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deletePermissionSetAssignment(id) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-set-assignments/${id}`, {
      method: 'DELETE',
    });
  }

  // AWS Accounts operations
  async getAwsAccounts() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts`);
  }

  async getAwsAccountsFromDB(params = {}) {
    const customerId = getCustomerIdFromSubdomain();
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);
    if (params.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest(`/api/v1/customers/${customerId}/aws-accounts${queryString}`);
  }

  async renameAWSAccount(accountId, newName) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/aws-accounts/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify({ name: newName }),
    });
  }

  async updateAccountOwners(accountId, ownerEmails) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/aws-accounts/${accountId}`, {
      method: 'PUT',
      body: JSON.stringify({ owner_emails: ownerEmails }),
    });
  }

  // Manual Account Onboarding operations
  async manualOnboardSingleAccount(accountData) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/onboard`, {
      method: 'POST',
      body: JSON.stringify(accountData),
    });
  }

  async manualOnboardBulkAccounts(accountsData) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/onboard/bulk`, {
      method: 'POST',
      body: JSON.stringify(accountsData),
    });
  }

  async onboardSSOAccounts(options = {}) {
    const customerId = getCustomerIdFromSubdomain();
    const queryParams = options.dryRun ? '?dryRun=true' : '';
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/onboard/sso-accounts${queryParams}`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  }

  async deboardAWSAccount(accountId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/aws-accounts/${accountId}/deboard`, {
      method: 'DELETE',
    });
  }

  async checkAWSAccountAssignments(accountId) {
    const customerId = getCustomerIdFromSubdomain();
    const queryParams = new URLSearchParams();
    queryParams.append('accountId', accountId);
    return this.makeRequest(`/api/v1/customers/${customerId}/permission-set-assignments?${queryParams.toString()}`);
  }

  async getSSOCacheStatus() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/sso-replication/cache/status`);
  }

  async initializeManagementAccount(accountData) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/init`, {
      method: 'POST',
      body: JSON.stringify(accountData),
    });
  }

  async getManagementAccount() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/init`);
  }

  async deleteManagementAccount() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/init`, {
      method: 'DELETE',
    });
  }

  async getOnboardingConfig() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/onboarding-config`);
  }

  async validateAccountAccess(accountId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/validate`, {
      method: 'POST',
      body: JSON.stringify({ accountId }),
    });
  }

  // Keycloak initialization operations
  async initializeKeycloak() {
    return this.makeRequest('/api/v1/init/keycloak', {
      method: 'POST',
    });
  }

  async getKeycloakStatus() {
    return this.makeRequest('/api/v1/init/keycloak');
  }

  async validateKeycloakConfiguration() {
    return this.makeRequest('/api/v1/init/keycloak/validate');
  }

  // Admin Management API functions
  async getCurrentAdmin() {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/auth/me`);
    }
    // App admin token validation (no customer subdomain)
    return this.makeRequest('/api/v1/auth/me');
  }

  async listAdmins() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/auth/admins`);
  }

  async promoteUserToAdmin(username, role = 'sso_admin') {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/auth/admins/promote`, {
      method: 'POST',
      body: JSON.stringify({ username, role }),
    });
  }

  async demoteAdmin(adminId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/auth/admins/demote`, {
      method: 'POST',
      body: JSON.stringify({ adminId }),
    });
  }

  async changeAdminRole(adminId, role) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/auth/admins/role`, {
      method: 'PUT',
      body: JSON.stringify({ adminId, role }),
    });
  }

  // CloudTrail access management
  async listCloudTrailUsers() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/cloudtrail/users`);
  }

  async toggleCloudTrailAccess(adminId, enabled) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/admins/${adminId}/cloudtrail-access`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  // Customer management operations
  async createCustomer(customerData) {
    return this.makeRequest('/api/v1/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  }

  async listCustomers() {
    return this.makeRequest('/api/v1/customers');
  }

  async getCustomer(customerId) {
    return this.makeRequest(`/api/v1/customers/${customerId}`);
  }

  async updateCustomer(customerId, customerData) {
    return this.makeRequest(`/api/v1/customers/${customerId}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }

  async deleteCustomer(customerId) {
    return this.makeRequest(`/api/v1/customers/${customerId}`, {
      method: 'DELETE',
    });
  }

  async getCustomerResourceCounts(customerId) {
    return this.makeRequest(`/api/v1/customers/${customerId}/resource-counts`);
  }

  // verifyTenantOU asks the backend (which proxies to prism-ops) whether the
  // expected CK-Tenant-<sanitized> OU exists in AWS for a SaaS shared-payer
  // customer. Returns { expected_name, found, ou_id, account_count, accounts }.
  // Optional opts let admins verify a configuration that hasn't been saved
  // yet: { customerName, mgmt, sharedPayer }.
  async verifyTenantOU(customerId, opts = {}) {
    const params = new URLSearchParams();
    if (opts.customerName !== undefined) params.set('customer_name', opts.customerName);
    if (opts.mgmt !== undefined) params.set('mgmt', opts.mgmt);
    if (opts.sharedPayer !== undefined) params.set('shared_payer', String(opts.sharedPayer));
    const qs = params.toString();
    return this.makeRequest(`/api/v1/customers/${customerId}/tenant-ou-status${qs ? `?${qs}` : ''}`);
  }

  async getCustomerRealmStatus(customerId) {
    return this.makeRequest(`/api/v1/customers/${customerId}/realm-status`);
  }

  async initializeCustomerAdmin(customerId, password) {
    return this.makeRequest(`/api/v1/customers/${customerId}/initialize-admin`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  // Reset customer super admin credentials (password and/or MFA)
  // This is a backdoor for app admins to help customers who are locked out
  async resetCustomerAdmin(customerId, { newPassword, resetMFA }) {
    return this.makeRequest(`/api/v1/customers/${customerId}/reset-admin`, {
      method: 'POST',
      body: JSON.stringify({ newPassword, resetMFA }),
    });
  }

  // SSO Replication operations
  async fetchSsoConfiguration() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/sso-replication/sso-config`);
  }

  async replicateUsersToKeycloak(dryRun = true) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/sso-replication/users?dryRun=${!!dryRun}`, {
      method: 'POST',
    });
  }

  async onboardAWSAccounts(dryRun = true) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/onboard/sso-accounts?dryRun=${!!dryRun}`, {
      method: 'POST',
    });
  }

  async replicateFullConfiguration(dryRun = true) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/sso-replication/full?dryRun=${!!dryRun}`, {
      method: 'POST',
    });
  }

  async getReplicationStatus() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/sso-replication/status`);
  }

  // Google Identity Provider operations

  // List all Google IdPs
  async listGoogleIdPs() {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/google`);
    }
    return this.makeRequest('/api/v1/identity-providers/google');
  }

  // Create a new Google IdP
  async createGoogleIdP(googleIdPData) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/google`, {
        method: 'POST',
        body: JSON.stringify(googleIdPData),
      });
    }
    return this.makeRequest('/api/v1/identity-providers/google', {
      method: 'POST',
      body: JSON.stringify(googleIdPData),
    });
  }

  // Get a specific Google IdP by alias
  async getGoogleIdPByAlias(alias) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/google/${encodeURIComponent(alias)}`);
    }
    return this.makeRequest(`/api/v1/identity-providers/google/${encodeURIComponent(alias)}`);
  }

  // Update a specific Google IdP by alias
  async updateGoogleIdP(alias, googleIdPData) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/google/${encodeURIComponent(alias)}`, {
        method: 'PUT',
        body: JSON.stringify(googleIdPData),
      });
    }
    return this.makeRequest(`/api/v1/identity-providers/google/${encodeURIComponent(alias)}`, {
      method: 'PUT',
      body: JSON.stringify(googleIdPData),
    });
  }

  // Delete a specific Google IdP by alias
  async deleteGoogleIdP(alias) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/google/${encodeURIComponent(alias)}`, {
        method: 'DELETE',
      });
    }
    return this.makeRequest(`/api/v1/identity-providers/google/${encodeURIComponent(alias)}`, {
      method: 'DELETE',
    });
  }

  async getIdPStatus() {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/status`);
    }
    return this.makeRequest('/api/v1/identity-providers/status');
  }

  async validateGoogleIdPConfig(validationData) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/google/validate`, {
        method: 'POST',
        body: JSON.stringify(validationData),
      });
    }
    return this.makeRequest('/api/v1/identity-providers/google/validate', {
      method: 'POST',
      body: JSON.stringify(validationData),
    });
  }

  // Microsoft Identity Provider operations
  async createMicrosoftIdP(microsoftIdPData) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/microsoft`, {
        method: 'POST',
        body: JSON.stringify(microsoftIdPData),
      });
    }
    return this.makeRequest('/api/v1/identity-providers/microsoft', {
      method: 'POST',
      body: JSON.stringify(microsoftIdPData),
    });
  }

  // List all Microsoft IdPs
  async listMicrosoftIdPs() {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/microsoft`);
    }
    return this.makeRequest('/api/v1/identity-providers/microsoft');
  }

  // Get a specific Microsoft IdP by alias
  async getMicrosoftIdPByAlias(alias) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/microsoft/${encodeURIComponent(alias)}`);
    }
    return this.makeRequest(`/api/v1/identity-providers/microsoft/${encodeURIComponent(alias)}`);
  }

  // Update a specific Microsoft IdP by alias
  async updateMicrosoftIdP(alias, microsoftIdPData) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/microsoft/${encodeURIComponent(alias)}`, {
        method: 'PUT',
        body: JSON.stringify(microsoftIdPData),
      });
    }
    return this.makeRequest(`/api/v1/identity-providers/microsoft/${encodeURIComponent(alias)}`, {
      method: 'PUT',
      body: JSON.stringify(microsoftIdPData),
    });
  }

  // Delete a specific Microsoft IdP by alias
  async deleteMicrosoftIdP(alias) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/microsoft/${encodeURIComponent(alias)}`, {
        method: 'DELETE',
      });
    }
    return this.makeRequest(`/api/v1/identity-providers/microsoft/${encodeURIComponent(alias)}`, {
      method: 'DELETE',
    });
  }

  async validateMicrosoftIdPConfig(validationData) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/microsoft/validate`, {
        method: 'POST',
        body: JSON.stringify(validationData),
      });
    }
    return this.makeRequest('/api/v1/identity-providers/microsoft/validate', {
      method: 'POST',
      body: JSON.stringify(validationData),
    });
  }

  // Custom OIDC Identity Provider methods
  async createCustomIdP(customIdPData) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/custom`, {
        method: 'POST',
        body: JSON.stringify(customIdPData),
      });
    }
    return this.makeRequest('/api/v1/identity-providers/custom', {
      method: 'POST',
      body: JSON.stringify(customIdPData),
    });
  }

  // List all Custom IdPs
  async listCustomIdPs() {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/custom`);
    }
    return this.makeRequest('/api/v1/identity-providers/custom');
  }

  // Get a specific Custom IdP by alias
  async getCustomIdPByAlias(alias) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/custom/${encodeURIComponent(alias)}`);
    }
    return this.makeRequest(`/api/v1/identity-providers/custom/${encodeURIComponent(alias)}`);
  }

  // Update a specific Custom IdP by alias
  async updateCustomIdP(alias, customIdPData) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/custom/${encodeURIComponent(alias)}`, {
        method: 'PUT',
        body: JSON.stringify(customIdPData),
      });
    }
    return this.makeRequest(`/api/v1/identity-providers/custom/${encodeURIComponent(alias)}`, {
      method: 'PUT',
      body: JSON.stringify(customIdPData),
    });
  }

  // Delete a specific Custom IdP by alias
  async deleteCustomIdP(alias) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/custom/${encodeURIComponent(alias)}`, {
        method: 'DELETE',
      });
    }
    return this.makeRequest(`/api/v1/identity-providers/custom/${encodeURIComponent(alias)}`, {
      method: 'DELETE',
    });
  }

  // Discover OIDC endpoints from a discovery URL
  async discoverOIDCEndpoints(discoveryUrl) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/custom/discover`, {
        method: 'POST',
        body: JSON.stringify({ discoveryUrl }),
      });
    }
    return this.makeRequest('/api/v1/identity-providers/custom/discover', {
      method: 'POST',
      body: JSON.stringify({ discoveryUrl }),
    });
  }

  async validateCustomIdPConfig(configData) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/identity-providers/custom/validate`, {
        method: 'POST',
        body: JSON.stringify(configData),
      });
    }
    return this.makeRequest('/api/v1/identity-providers/custom/validate', {
      method: 'POST',
      body: JSON.stringify(configData),
    });
  }

  // Realm Login Settings methods
  async getRealmLoginSettings() {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/realm/login-settings`);
    }
    throw new Error('Customer context is required for realm login settings');
  }

  async updateRealmLoginSettings(idpOnlyLogin) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/realm/login-settings`, {
        method: 'PUT',
        body: JSON.stringify({ idpOnlyLogin }),
      });
    }
    throw new Error('Customer context is required for realm login settings');
  }

  // SSO Session Settings methods
  async getSSOSessionSettings() {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/realm/sso-session-settings`);
    }
    throw new Error('Customer context is required for SSO session settings');
  }

  async updateSSOSessionSettings(timeoutMinutes) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/realm/sso-session-settings`, {
        method: 'PUT',
        body: JSON.stringify({ timeoutMinutes }),
      });
    }
    throw new Error('Customer context is required for SSO session settings');
  }

  // Per-user MFA management methods
  async getUsersMFAStatus() {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/realm/mfa-status`);
    }
    throw new Error('Customer context is required for MFA status');
  }

  async enforceMFA(userIds, mfaType = 'otp') {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/realm/mfa-enforce`, {
        method: 'PUT',
        body: JSON.stringify({ userIds, mfaType }),
      });
    }
    throw new Error('Customer context is required for MFA enforcement');
  }

  async removeMFAEnforcement(userIds, mfaType = 'otp') {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/realm/mfa-remove`, {
        method: 'PUT',
        body: JSON.stringify({ userIds, mfaType }),
      });
    }
    throw new Error('Customer context is required for MFA enforcement removal');
  }

  async getPasskeysSettings() {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/realm/passkeys-settings`);
    }
    throw new Error('Customer context is required for passkeys settings');
  }

  async updatePasskeysSettings(enabled) {
    const customerId = getCustomerIdFromSubdomain();
    if (customerId) {
      return this.makeRequest(`/api/v1/customers/${customerId}/realm/passkeys-settings`, {
        method: 'PUT',
        body: JSON.stringify({ enabled }),
      });
    }
    throw new Error('Customer context is required for passkeys settings');
  }

  // Custom SAML Application methods
  async createCustomApplication(customerId, applicationData) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications`, {
      method: 'POST',
      body: JSON.stringify(applicationData),
    });
  }

  async getCustomApplication(customerId, applicationId) {
    // Use provided customerId, or fallback to subdomain
    const resolvedCustomerId = customerId || getCustomerIdFromSubdomain();
    if (!resolvedCustomerId) {
      throw new Error('Customer ID is required for fetching custom applications');
    }
    return this.makeRequest(`/api/v1/customers/${resolvedCustomerId}/custom-applications/${encodeURIComponent(applicationId)}`);
  }

  async listCustomApplications(customerId) {
    const resolvedCustomerId = customerId || getCustomerIdFromSubdomain();
    if (!resolvedCustomerId) {
      throw new Error('Customer ID is required for listing custom applications');
    }
    return this.makeRequest(`/api/v1/customers/${resolvedCustomerId}/custom-applications`);
  }

  async updateCustomApplication(customerId, applicationId, applicationData) {
    const resolvedCustomerId = customerId || getCustomerIdFromSubdomain();
    if (!resolvedCustomerId) {
      throw new Error('Customer ID is required for updating custom applications');
    }
    return this.makeRequest(`/api/v1/customers/${resolvedCustomerId}/custom-applications/${encodeURIComponent(applicationId)}`, {
      method: 'PUT',
      body: JSON.stringify(applicationData),
    });
  }

  async deleteCustomApplication(customerId, applicationId) {
    // Use provided customerId, or fallback to subdomain
    const resolvedCustomerId = customerId || getCustomerIdFromSubdomain();
    if (!resolvedCustomerId) {
      throw new Error('Customer ID is required for deleting custom applications');
    }
    return this.makeRequest(`/api/v1/customers/${resolvedCustomerId}/custom-applications/${encodeURIComponent(applicationId)}`, {
      method: 'DELETE',
    });
  }

  async uploadCustomApplicationIcon(customerId, applicationId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.makeRequest(
      `/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/icon`,
      {
        method: 'PUT',
        body: formData,
        isFormData: true,
      }
    );
  }

  async deleteCustomApplicationIcon(customerId, applicationId) {
    return this.makeRequest(
      `/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/icon`,
      { method: 'DELETE' }
    );
  }

  async createCustomApplicationMapper(customerId, applicationId, mapperData) {
    return this.makeRequest(
      `/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/mappers`,
      {
        method: 'POST',
        body: JSON.stringify(mapperData),
      }
    );
  }

  async getCustomApplicationMappers(customerId, applicationId) {
    return this.makeRequest(
      `/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/mappers`
    );
  }

  async deleteCustomApplicationMapper(customerId, applicationId, mapperId) {
    return this.makeRequest(
      `/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/mappers/${encodeURIComponent(mapperId)}`,
      { method: 'DELETE' }
    );
  }

  // Custom Application Metadata
  async getCustomApplicationMetadata(customerId, applicationId) {
    const resolvedCustomerId = customerId || getCustomerIdFromSubdomain();
    if (!resolvedCustomerId) {
      throw new Error('Customer ID is required for fetching metadata');
    }
    const response = await fetch(
      `${API_BASE_URL}/api/v1/customers/${resolvedCustomerId}/custom-applications/${encodeURIComponent(applicationId)}/metadata`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/xml',
          ...this.getAuthHeaders(),
        },
      }
    );
    if (!response.ok) {
      throw new Error('Failed to fetch metadata');
    }
    return response.text();
  }

  // Custom Application Role Management
  async createCustomApplicationRole(customerId, applicationId, roleData) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/roles`, {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
  }

  async listCustomApplicationRoles(customerId, applicationId) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/roles`);
  }

  async deleteCustomApplicationRole(customerId, applicationId, roleName) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/roles/${encodeURIComponent(roleName)}`, {
      method: 'DELETE',
    });
  }

  async assignRoleToUser(customerId, applicationId, roleName, username) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/roles/${encodeURIComponent(roleName)}/users/${encodeURIComponent(username)}`, {
      method: 'POST',
    });
  }

  async removeRoleFromUser(customerId, applicationId, roleName, username) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/roles/${encodeURIComponent(roleName)}/users/${encodeURIComponent(username)}`, {
      method: 'DELETE',
    });
  }

  async listRoleUsers(customerId, applicationId, roleName) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/roles/${encodeURIComponent(roleName)}/users`);
  }

  // Custom Application Group Role Assignment methods
  async assignRoleToGroup(customerId, applicationId, roleName, groupName) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/roles/${encodeURIComponent(roleName)}/groups/${encodeURIComponent(groupName)}`, {
      method: 'POST',
    });
  }

  async removeRoleFromGroup(customerId, applicationId, roleName, groupName) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/roles/${encodeURIComponent(roleName)}/groups/${encodeURIComponent(groupName)}`, {
      method: 'DELETE',
    });
  }

  async listRoleGroups(customerId, applicationId, roleName) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/roles/${encodeURIComponent(roleName)}/groups`);
  }

  // Custom Application User Assignment methods
  async assignUsersToApplication(customerId, applicationId, usernames) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/users`, {
      method: 'POST',
      body: JSON.stringify({ usernames }),
    });
  }

  async listApplicationUsers(customerId, applicationId) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/users`);
  }

  async removeUserFromApplication(customerId, applicationId, username) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/users/${encodeURIComponent(username)}`, {
      method: 'DELETE',
    });
  }

  // Custom Application Group Assignment methods
  async assignGroupsToApplication(customerId, applicationId, groupNames) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/groups`, {
      method: 'POST',
      body: JSON.stringify({ groupNames }),
    });
  }

  async listApplicationGroups(customerId, applicationId) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/groups`);
  }

  async removeGroupFromApplication(customerId, applicationId, groupName) {
    return this.makeRequest(`/api/v1/customers/${customerId}/custom-applications/${encodeURIComponent(applicationId)}/groups/${encodeURIComponent(groupName)}`, {
      method: 'DELETE',
    });
  }

  // Customer-specific SCIM operations
  async generateCustomerSCIMToken(customerId, expiresInDays = null) {
    const endpoint = `/api/v1/customers/${customerId}/scim/tokens/generate`;
    const body = {};
    if (expiresInDays !== null) {
      body.expiresIn = expiresInDays;
    }

    return this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async listCustomerSCIMTokens(customerId) {
    return this.makeRequest(`/api/v1/customers/${customerId}/scim/tokens/list`);
  }

  async revokeCustomerSCIMToken(customerId, token) {
    return this.makeRequest(`/api/v1/customers/${customerId}/scim/tokens/revoke`, {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async deleteCustomerSCIMToken(customerId, token) {
    return this.makeRequest(`/api/v1/customers/${customerId}/scim/tokens/delete`, {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    });
  }

  // Customer-specific Google Workspace sync
  async getGoogleSyncConfig(customerId) {
    return this.makeRequest(`/api/v1/customers/${customerId}/google-sync/config`);
  }

  async saveGoogleSyncConfig(customerId, config) {
    return this.makeRequest(`/api/v1/customers/${customerId}/google-sync/config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async deleteGoogleSyncConfig(customerId) {
    return this.makeRequest(`/api/v1/customers/${customerId}/google-sync/config`, {
      method: 'DELETE',
    });
  }

  async triggerGoogleSync(customerId) {
    return this.makeRequest(`/api/v1/customers/${customerId}/google-sync/trigger`, {
      method: 'POST',
    });
  }

  // Get current customer information
  async getCurrentCustomer() {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Customer context not available');
    }

    const endpoint = `/api/v1/customers/${customerId}/me`;
    return this.makeRequest(endpoint);
  }

  async changeUserPassword(username, newPassword, temporary = false) {
    const endpoint = `${getUsersEndpoint()}/${encodeURIComponent(username)}/change-password`;
    return this.makeRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify({ newPassword, temporary }),
    });
  }

  async getUserEffectiveAssignments(username) {
    const endpoint = `${getUsersEndpoint()}/${encodeURIComponent(username)}/effective-assignments`;
    return this.makeRequest(endpoint);
  }

  async getGroupAssignments(groupName) {
    const endpoint = `${getGroupsEndpoint()}/${encodeURIComponent(groupName)}/assignments`;
    return this.makeRequest(endpoint);
  }

  // Static Token API methods for Terraform provider
  async createStaticToken(description, expiresAt = null) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Static tokens are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/static-tokens`, {
      method: 'POST',
      body: JSON.stringify({ description, expiresAt }),
    });
  }

  async listStaticTokens() {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Static tokens are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/static-tokens`);
  }

  async revokeStaticToken(tokenId) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Static tokens are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/static-tokens/${tokenId}`, {
      method: 'DELETE',
    });
  }

  // Audit Log API methods
  async getAuditLogs(filters = {}) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Audit logs are only available for customer accounts');
    }

    const queryParams = new URLSearchParams();

    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.actionTypes && filters.actionTypes.length > 0) {
      filters.actionTypes.forEach(t => queryParams.append('actionType', t));
    }
    if (filters.resourceTypes && filters.resourceTypes.length > 0) {
      filters.resourceTypes.forEach(t => queryParams.append('resourceType', t));
    }
    if (filters.statusCodes && filters.statusCodes.length > 0) {
      filters.statusCodes.forEach(s => queryParams.append('status', s));
    }
    if (filters.statusFilter) queryParams.append('statusFilter', filters.statusFilter);
    if (filters.userId) queryParams.append('userId', filters.userId);
    if (filters.endpoint) queryParams.append('endpoint', filters.endpoint);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.pageSize) queryParams.append('pageSize', filters.pageSize);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest(`/api/v1/customers/${customerId}/audit-logs${queryString}`);
  }

  async getAuditLog(logId) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Audit logs are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/audit-logs/${logId}`);
  }

  async getAuditStats(days = 30) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Audit statistics are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/audit-logs/stats?days=${days}`);
  }

  async getAuditFilterOptions() {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Audit logs are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/audit-logs/filter-options`);
  }

  async getAuditSettings() {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Audit settings are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/audit-settings`);
  }

  async updateAuditSettings(logReadRequests) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Audit settings are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/audit-settings`, {
      method: 'PUT',
      body: JSON.stringify({ logReadRequests }),
    });
  }

  // Access Log API methods
  async getAccessLogs(filters = {}) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Access logs are only available for customer accounts');
    }

    const queryParams = new URLSearchParams();

    if (filters.startDate) queryParams.append('startDate', filters.startDate);
    if (filters.endDate) queryParams.append('endDate', filters.endDate);
    if (filters.userEmail) queryParams.append('userEmail', filters.userEmail);
    if (filters.awsAccountId) queryParams.append('awsAccountId', filters.awsAccountId);
    if (filters.awsAccountName) queryParams.append('awsAccountName', filters.awsAccountName);
    if (filters.permissionSet) queryParams.append('permissionSet', filters.permissionSet);
    if (filters.loginType) queryParams.append('loginType', filters.loginType);
    if (filters.applicationName) queryParams.append('applicationName', filters.applicationName);
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.pageSize) queryParams.append('pageSize', filters.pageSize);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest(`/api/v1/customers/${customerId}/access-logs${queryString}`);
  }

  async getAccessLog(logId) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Access logs are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/access-logs/${logId}`);
  }

  async getAccessStats(days = 30) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Access statistics are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/access-logs/stats?days=${days}`);
  }

  async getAccessLogFilterOptions() {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Access logs are only available for customer accounts');
    }

    return this.makeRequest(`/api/v1/customers/${customerId}/access-logs/filter-options`);
  }

  // S3 Export Configuration API methods

  async getS3ExportConfig() {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('S3 export settings are only available for customer accounts');
    }
    return this.makeRequest(`/api/v1/customers/${customerId}/s3-export/config`);
  }

  async updateS3ExportConfig(config) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('S3 export settings are only available for customer accounts');
    }
    return this.makeRequest(`/api/v1/customers/${customerId}/s3-export/config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async deleteS3ExportConfig() {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('S3 export settings are only available for customer accounts');
    }
    return this.makeRequest(`/api/v1/customers/${customerId}/s3-export/config`, {
      method: 'DELETE',
    });
  }

  async testS3Connection(bucketName, prefix) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('S3 export settings are only available for customer accounts');
    }
    return this.makeRequest(`/api/v1/customers/${customerId}/s3-export/test`, {
      method: 'POST',
      body: JSON.stringify({ bucketName, prefix }),
    });
  }

  async triggerS3Export() {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('S3 export is only available for customer accounts');
    }
    return this.makeRequest(`/api/v1/customers/${customerId}/s3-export/trigger`, {
      method: 'POST',
    });
  }

  async getS3ExportHistory(params = {}) {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('S3 export history is only available for customer accounts');
    }

    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.pageSize) queryParams.append('pageSize', params.pageSize);

    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    return this.makeRequest(`/api/v1/customers/${customerId}/s3-export/history${queryString}`);
  }

  async getS3CallerIdentity() {
    const customerId = getCustomerIdFromSubdomain();
    if (!customerId) {
      throw new Error('Caller identity is only available for customer accounts');
    }
    return this.makeRequest(`/api/v1/customers/${customerId}/s3-export/caller-identity`);
  }

  // SaaS Connection (self-hosted pairing with CloudKeeper)
  async getSaaSConnection() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/saas-connection`);
  }

  async redeemSaaSCode(saasUrl, code) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/saas-connection/redeem`, {
      method: 'POST',
      body: JSON.stringify({ saas_url: saasUrl, code }),
    });
  }

  async testSaaSConnection() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/saas-connection/test`);
  }

  async disconnectSaaS() {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/saas-connection`, {
      method: 'DELETE',
    });
  }

  // Org Services
  async getOrgStructure(managementAccountId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/structure`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId }),
    });
  }

  async createOU(managementAccountId, parentId, name) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/create-ou`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, parent_id: parentId, name }),
    });
  }

  async deleteOU(managementAccountId, ouId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/delete-ou`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, ou_id: ouId }),
    });
  }

  async moveAccount(managementAccountId, accountId, sourceParentId, destParentId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/move-account`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, account_id: accountId, source_parent_id: sourceParentId, dest_parent_id: destParentId }),
    });
  }

  async getEnabledPolicyTypes(managementAccountId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/enabled-policy-types`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId }),
    });
  }

  async listOrgPolicies(managementAccountId, policyType) {
    const customerId = getCustomerIdFromSubdomain();
    const body = { management_account_id: managementAccountId };
    if (policyType) body.policy_type = policyType;
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async createOrgPolicy(managementAccountId, name, description, content, policyType) {
    const customerId = getCustomerIdFromSubdomain();
    const body = { management_account_id: managementAccountId, name, description, content };
    if (policyType) body.policy_type = policyType;
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/create`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async deleteOrgPolicy(managementAccountId, policyId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/delete`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, policy_id: policyId }),
    });
  }

  async updateOrgPolicy(managementAccountId, policyId, name, description, content) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/update`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, policy_id: policyId, name, description, content }),
    });
  }

  async attachOrgPolicy(managementAccountId, policyId, targetId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/attach`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, policy_id: policyId, target_id: targetId }),
    });
  }

  async detachOrgPolicy(managementAccountId, policyId, targetId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/detach`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, policy_id: policyId, target_id: targetId }),
    });
  }

  async listOrgPoliciesForTarget(managementAccountId, targetId, policyType) {
    const customerId = getCustomerIdFromSubdomain();
    const body = { management_account_id: managementAccountId, target_id: targetId };
    if (policyType) body.policy_type = policyType;
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/for-target`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // countOrgPoliciesForTarget returns the true AWS-side attachment count for
  // a target (for quota display). Unlike listOrgPoliciesForTarget, this does
  // not filter by tenant ownership — it reflects AWS's real 5-per-target limit.
  async countOrgPoliciesForTarget(managementAccountId, targetId, policyType) {
    const customerId = getCustomerIdFromSubdomain();
    const body = { management_account_id: managementAccountId, target_id: targetId };
    if (policyType) body.policy_type = policyType;
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/count-for-target`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async describeOrgPolicy(managementAccountId, policyId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/describe`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, policy_id: policyId }),
    });
  }

  async listTargetsForOrgPolicy(managementAccountId, policyId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/targets`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, policy_id: policyId }),
    });
  }

  async listTagsForPolicy(managementAccountId, policyId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/tags`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, policy_id: policyId }),
    });
  }

  async tagPolicy(managementAccountId, policyId, tags) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/tag`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, policy_id: policyId, tags }),
    });
  }

  async untagPolicy(managementAccountId, policyId, tagKeys) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/org/policies/untag`, {
      method: 'POST',
      body: JSON.stringify({ management_account_id: managementAccountId, policy_id: policyId, tag_keys: tagKeys }),
    });
  }

  // Account Contact Management
  async getAccountContacts(accountId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/${accountId}/contacts/get`, {
      method: 'POST',
    });
  }

  async updatePrimaryContact(accountId, contact) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/${accountId}/contacts/primary`, {
      method: 'PUT',
      body: JSON.stringify(contact),
    });
  }

  async updateAlternateContact(accountId, type, contact) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/${accountId}/contacts/${type}`, {
      method: 'PUT',
      body: JSON.stringify(contact),
    });
  }

  async deleteAlternateContact(accountId, type) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/${accountId}/contacts/${type}`, {
      method: 'DELETE',
    });
  }

  async startPrimaryEmailUpdate(accountId, email) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/${accountId}/contacts/email/start`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async acceptPrimaryEmailUpdate(accountId, email, otp) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/accounts/${accountId}/contacts/email/accept`, {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });
  }

  // Billing Status
  async checkBillingStatus(accountIds) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/billing/status`, {
      method: 'POST',
      body: JSON.stringify({ account_ids: accountIds }),
    });
  }

  async selfOnboardToBilling(accountId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/billing/self-onboard`, {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId }),
    });
  }

  async pollOnboardStatus(accountId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/billing/self-onboard/${accountId}/status`);
  }

  async batchPollOnboardStatus(accountIds) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/billing/self-onboard/status`, {
      method: 'POST',
      body: JSON.stringify({ account_ids: accountIds }),
    });
  }

  async leaveOrganization(accountId) {
    const customerId = getCustomerIdFromSubdomain();
    return this.makeRequest(`/api/v1/customers/${customerId}/billing/leave-org`, {
      method: 'POST',
      body: JSON.stringify({ account_id: accountId, confirm_delete_org: true }),
    });
  }

  // Impersonation (app-admin only)
  async impersonateCustomerAdmin(customerId) {
    return this.makeRequest(`/api/v1/app-admin/impersonate/${customerId}`, {
      method: 'POST',
    });
  }

  // Analytics API methods (SaaS app-admin only)
  async getAnalyticsPlatformStats(source = 'combined') {
    return this.makeRequest(`/api/v1/app-admin/analytics/platform-stats?source=${encodeURIComponent(source)}`);
  }

  async getAnalyticsLoginStats(days = 30, source = 'combined') {
    return this.makeRequest(`/api/v1/app-admin/analytics/login-stats?days=${days}&source=${encodeURIComponent(source)}`);
  }

  async getAnalyticsAccessLogs(params = {}) {
    const merged = { source: 'combined', ...params };
    const query = new URLSearchParams(merged).toString();
    return this.makeRequest(`/api/v1/app-admin/analytics/access-logs${query ? '?' + query : ''}`);
  }
}

const apiService = new ApiService();
export default apiService;